// src/lib/normalizeSettings.js
// Utilitaires partagés par MenuRenderer, MenuPagePublic et MenuPageEditor.

/**
 * Décode `settings` même s'il est encodé plusieurs fois en JSON (string de string).
 * Renvoie toujours un objet.
 */
export function normalizeSettings(raw) {
  let value = raw
  for (let i = 0; i < 3 && typeof value === 'string'; i++) {
    try {
      value = JSON.parse(value)
    } catch {
      return {}
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
}

/** Transforme une description (string ou objet i18n) en string. */
function normalizeDescription(desc) {
  if (typeof desc === 'string') return desc
  if (desc && typeof desc === 'object') {
    if (typeof desc.fr === 'string') return desc.fr
    const first = Object.values(desc).find((v) => typeof v === 'string')
    return first || ''
  }
  return ''
}

/** Transforme le prix (price ou price_cents) en string. */
function normalizePrice(it) {
  if (it.price !== undefined && it.price !== null && it.price !== '') {
    return String(it.price)
  }
  if (it.price_cents !== undefined && it.price_cents !== null) {
    return String(it.price_cents / 100)
  }
  return ''
}

/**
 * Récupère les catégories où qu'elles se trouvent et garantit
 * que chaque catégorie et chaque plat possède un `id`, un tableau `items`,
 * une `description` en string et un `price` en string.
 */
export function extractCategories(page) {
  const settings = normalizeSettings(page?.settings)

  let raw =
    (Array.isArray(settings.categories) && settings.categories.length
      ? settings.categories
      : null) ??
    page?.categories ??
    page?.menu?.categories ??
    settings.categories ??
    []

  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw)
    } catch {
      raw = []
    }
  }
  if (!Array.isArray(raw)) return []

  return raw
    .filter((cat) => cat && typeof cat === 'object')
    .map((cat, ci) => {
      let items = cat.items
      if (typeof items === 'string') {
        try {
          items = JSON.parse(items)
        } catch {
          items = []
        }
      }
      return {
        ...cat,
        id: cat.id ?? `cat-${ci}`,
        name: typeof cat.name === 'string' ? cat.name : normalizeDescription(cat.name),
        items: (Array.isArray(items) ? items : [])
          .filter((it) => it && typeof it === 'object')
          .map((it, ii) => ({
            ...it,
            id: it.id ?? `item-${ci}-${ii}`,
            name: typeof it.name === 'string' ? it.name : normalizeDescription(it.name),
            description: normalizeDescription(it.description),
            price: normalizePrice(it),
            image: it.image || '',
          })),
      }
    })
}

/* ============================================================
   AJOUT — slugify (utilisé par MenuPageEditor, LoyaltyPageEditor,
   GoogleReviewPageEditor pour générer un slug si vide)
   ============================================================ */

/**
 * Transforme un titre en slug URL-safe.
 * "Mon Café du Coin !" → "mon-cafe-du-coin"
 */
export function slugify(str) {
  return (str || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // supprime les accents
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}